import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import date from 'date-and-time';

import { Alert } from '../base/Alert';
import { Button } from '../base/Button';
import { Form } from '../base/Form/Form';
import { Modal } from '../../layouts/Modal';
import { FormSelect } from '../base/Form/FormSelect';
import { SectionLoading } from '../shared/SectionLoading';
import { FormTextField } from '../base/Form/FormTextField';
import { useUserContext } from '../../contexts/UserContext';
import useAppliances from '../../data/appliance/useAppliances';

import useApplianceTypes from '../../data/appliance/useApplianceTypes';
import usePlanetDevices from '../../data/planetDevice/usePlanetDevices';
import useApplianceMutations from '../../data/appliance/useApplianceMutations';
import { hardwareSettingsFormatters } from '../../utils/hardwareSettingsFormatters';
import {
  ApplianceModbusProtocol,
  IAppliance,
  IApplianceCreateUpdateFormFields,
  IHeatPumpAppliance,
} from '../../types/Appliance/Appliance';
import {
  IPlanetDevice,
  IPlanetDeviceWithFingotiDevice,
} from '../../types/PlanetDevice/PlanetDevice';
import { FormManufacturerSelect } from '../shared/FormManufacturerSelect';
import { FormModelSelect } from '../shared/FormModelSelect';
import { HeatPumpSettings } from './CreateEditAppliance/HeatPumpSettings';
import { ModbusSettings } from './CreateEditAppliance/ModbusSettings';
import usePlanetDeviceMutations from '../../data/planetDevice/usePlanetDeviceMutations';
import usePlanetDevicesWithFingotiDevice from '../../data/planetDevice/usePlanetDevicesWithFingotiDevice';

export interface CreateEditApplianceProps {
  open: boolean;
  onClose: Dispatch<SetStateAction<boolean>>;
  appliance?: IAppliance | IHeatPumpAppliance;
}

const isHeatPumpAppliance = (
  appliance: IAppliance
): appliance is IHeatPumpAppliance => {
  return (appliance as IHeatPumpAppliance).heatPumpSettings !== undefined;
};

export function CreateEditAppliance({
  open,
  onClose,
  appliance,
}: CreateEditApplianceProps) {
  const { organisationId } = useUserContext();
  const { appliances: allAppliances } = useAppliances();
  const { planetDevicesWithFingotiDevice: allPlanetDevices } =
    usePlanetDevicesWithFingotiDevice();
  const { createAppliance, updateAppliance } = useApplianceMutations();
  const { setupPlanetDevice } = usePlanetDeviceMutations();
  const { applianceTypes } = useApplianceTypes();
  const [planetDevices, setPlanetDevices] = useState<
    IPlanetDeviceWithFingotiDevice[] | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);

  const handleClose = () => {
    onClose(false);
  };

  const sendDeviceSetup = (deviceId: string) => {
    setupPlanetDevice.mutate({
      devices: [deviceId],
    });
  };

  const handleCreate = (data: IApplianceCreateUpdateFormFields) => {
    createAppliance.mutate(
      {
        ...data,
        organisationId: organisationId!,
        deviceId: data.deviceId !== '0' ? data.deviceId : undefined,
        installedDate:
          data.installedDate !== '' ? data.installedDate : undefined,
        serviceDue: data.serviceDue !== '' ? data.serviceDue : undefined,
        hardwareModelId: data.hardwareModelId,
        hardwareManufacturerId: data.hardwareManufacturerId,
        hardwareTypeId: data.hardwareTypeId,
        heatPumpSettings: data.heatPumpSettings
          ? {
              assumedFlowRate: data.heatPumpSettings.assumedFlowRate
                ? hardwareSettingsFormatters.heatPumpSettings.flowRate.toInteger(
                    data.heatPumpSettings.assumedFlowRate
                  )
                : 0,
              antifreeze: data.heatPumpSettings.antifreeze
                ? parseInt(data.heatPumpSettings.antifreeze)
                : 0,
            }
          : undefined,
        modbusSettings: data.modbusSettings
          ? {
              protocol:
                data.modbusSettings.protocol === 'RTU'
                  ? ApplianceModbusProtocol.RTU
                  : ApplianceModbusProtocol.TCPIP,
              rtuAddress: data.modbusSettings.rtuAddress
                ? parseInt(data.modbusSettings.rtuAddress)
                : undefined,
              ipAddress: data.modbusSettings.ipAddress,
              port: parseInt(data.modbusSettings.port ?? '502'),
            }
          : undefined,
      },
      {
        onSuccess: (response) => {
          if (response.data.data[0].deviceId) {
            sendDeviceSetup(response.data.data[0].deviceId);
          }
        },
      }
    );
    handleClose();
  };

  const handleUpdate = (data: IApplianceCreateUpdateFormFields) => {
    updateAppliance.mutate(
      {
        ...appliance,
        ...data,
        deviceId: data.deviceId !== '0' ? data.deviceId : undefined,
        serviceDue: data.serviceDue !== '' ? data.serviceDue : undefined,
        hardwareModelId: data.hardwareModelId,
        hardwareManufacturerId: data.hardwareManufacturerId,
        hardwareTypeId: data.hardwareTypeId,
        heatPumpSettings: data.heatPumpSettings
          ? {
              assumedFlowRate: data.heatPumpSettings.assumedFlowRate
                ? hardwareSettingsFormatters.heatPumpSettings.flowRate.toInteger(
                    data.heatPumpSettings.assumedFlowRate
                  )
                : 0,
              antifreeze: data.heatPumpSettings.antifreeze
                ? parseInt(data.heatPumpSettings.antifreeze)
                : 0,
            }
          : undefined,
        modbusSettings: data.modbusSettings
          ? {
              protocol:
                data.modbusSettings.protocol === 'RTU'
                  ? ApplianceModbusProtocol.RTU
                  : ApplianceModbusProtocol.TCPIP,
              rtuAddress: data.modbusSettings.rtuAddress
                ? parseInt(data.modbusSettings.rtuAddress)
                : undefined,
              ipAddress: data.modbusSettings.ipAddress,
              port: parseInt(data.modbusSettings.port ?? '502'),
            }
          : undefined,
      },
      {
        onSuccess: (response) => {
          if (response.data.data[0].deviceId) {
            sendDeviceSetup(response.data.data[0].deviceId);
          }
        },
      }
    );
    handleClose();
  };

  useEffect(() => {
    if (
      !allPlanetDevices.isLoading &&
      !allAppliances.isLoading &&
      allPlanetDevices.data &&
      allAppliances.data
    ) {
      let currentDevice;

      let filteredDevices = allPlanetDevices.data.filter(
        (device) =>
          !allAppliances.data.some(
            (hardware) => hardware.deviceId === device.id
          )
      );

      if (appliance) {
        currentDevice = allPlanetDevices.data.find(
          (device) => device.id === appliance?.deviceId
        );
        currentDevice && filteredDevices.push(currentDevice);
      }

      setPlanetDevices(filteredDevices);
      setLoading(false);
    }
  }, [
    appliance,
    allPlanetDevices.isLoading,
    allAppliances.isLoading,
    allPlanetDevices.data,
    allAppliances.data,
  ]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`${appliance ? 'Manage' : 'Install'} Appliance`}
      description={`${appliance ? 'Manage' : 'Install'} an Appliance.`}
    >
      {applianceTypes.isLoading || allAppliances.isLoading || loading ? (
        <SectionLoading />
      ) : !allAppliances.isError ? (
        <>
          {appliance?.setupRequired && (
            <Alert
              className="my-6"
              severity="warn"
              title="Pending Changes"
              message="This appliance has pending changes, please be aware that information displayed below may not reflect the actual state of the device."
            />
          )}
          <Form<IApplianceCreateUpdateFormFields>
            defaultValues={
              appliance
                ? {
                    name: appliance.name,
                    reference: appliance.reference,
                    installedDate:
                      appliance.serviceDue &&
                      date.format(
                        new Date(appliance.installedDate),
                        'YYYY-MM-DD'
                      ),
                    serviceDue:
                      appliance.serviceDue &&
                      date.format(new Date(appliance.serviceDue), 'YYYY-MM-DD'),
                    deviceId: appliance.deviceId ? appliance.deviceId : '0',
                    hardwareModelId: appliance.hardwareModelId,
                    hardwareManufacturerId: appliance.hardwareManufacturerId,
                    hardwareTypeId: appliance.hardwareTypeId,
                    heatPumpSettings: isHeatPumpAppliance(appliance)
                      ? {
                          assumedFlowRate: appliance.heatPumpSettings
                            .assumedFlowRate
                            ? hardwareSettingsFormatters.heatPumpSettings.flowRate.toDisplay(
                                appliance.heatPumpSettings.assumedFlowRate
                              )
                            : '0.0',
                          antifreeze: appliance.heatPumpSettings.antifreeze
                            ? String(appliance.heatPumpSettings.antifreeze)
                            : '0',
                        }
                      : undefined,
                    modbusSettings: {
                      protocol:
                        appliance.modbusSettings.protocol ===
                        ApplianceModbusProtocol.RTU
                          ? 'RTU'
                          : 'TCP/IP',
                      rtuAddress: appliance.modbusSettings.rtuAddress
                        ? String(appliance.modbusSettings.rtuAddress)
                        : '1',
                      ipAddress: appliance.modbusSettings.ipAddress
                        ? appliance.modbusSettings.ipAddress
                        : '0.0.0.0',
                      port: appliance.modbusSettings.port
                        ? String(appliance.modbusSettings.port)
                        : '502',
                    },
                  }
                : {
                    installedDate: date.format(new Date(), 'YYYY-MM-DD'),
                    serviceDue: date.format(
                      date.addYears(new Date(), 1),
                      'YYYY-MM-DD'
                    ),
                    hardwareTypeId: '0',
                    hardwareManufacturerId: '0',
                    hardwareModelId: '0',
                    heatPumpSettings: {
                      assumedFlowRate: '0.0',
                      antifreeze: '0',
                    },
                    modbusSettings: {
                      protocol: 'RTU',
                      rtuAddress: '1',
                      ipAddress: '192.168.1.1',
                      port: '502',
                    },
                  }
            }
            onSubmit={appliance ? handleUpdate : handleCreate}
            className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-4 sm:gap-6 sm:gap-y-4"
          >
            {({ register, watch, setValue, resetField }) => (
              <>
                <FormTextField
                  {...register('name', { required: true })}
                  label="Appliance Name"
                />
                <FormTextField
                  {...register('reference')}
                  label="Unique Reference/Identifier"
                />
                <FormTextField
                  {...register('installedDate', {
                    required: true,
                    onChange: (e) => {
                      setValue(
                        'serviceDue',
                        date.format(
                          date.addYears(
                            date.parse(e.target.value, 'YYYY-MM-DD'),
                            1
                          ),
                          'YYYY-MM-DD'
                        )
                      );
                    },
                  })}
                  label="Appliance Installation Date"
                  type="date"
                  disabled={appliance !== undefined}
                />
                <FormTextField
                  {...register('serviceDue', {
                    validate: {
                      notLessThanStart: (serviceDue, formValues) =>
                        !serviceDue || !formValues.installedDate
                          ? true
                          : serviceDue > formValues.installedDate
                            ? true
                            : 'Service Due must be ahead of install date',
                    },
                  })}
                  label="Appliance Service Due Date"
                  type="date"
                />
                <FormSelect
                  {...register('deviceId', {
                    required: true,
                  })}
                  label="Atmo Device"
                  onBlur={() => allPlanetDevices.refetch()}
                >
                  {planetDevices!.map((device) => (
                    <option
                      key={device.id}
                      value={device.id}
                      disabled={!device.gateway?.device.cloud.connected}
                    >
                      {device.serialNumber}
                    </option>
                  ))}
                  <option value="0">No Device</option>
                </FormSelect>
                <fieldset disabled={appliance !== undefined}>
                  <FormSelect
                    {...register('hardwareTypeId', {
                      required: true,
                      validate: (val) =>
                        val !== '0'
                          ? true
                          : 'Type of Renewable Technology is required',
                      onChange: () => {
                        resetField('hardwareManufacturerId');
                        resetField('hardwareModelId');
                      },
                    })}
                    label="Type of Renewable Technology"
                  >
                    <option selected disabled value="0">
                      Please Select
                    </option>
                    {applianceTypes.data?.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </FormSelect>
                </fieldset>
                <fieldset
                  disabled={
                    appliance !== undefined || watch('hardwareTypeId') === '0'
                  }
                >
                  <FormManufacturerSelect
                    {...register('hardwareManufacturerId', {
                      required: true,
                      onChange: () => {
                        resetField('hardwareModelId');
                      },
                    })}
                    type={watch('hardwareTypeId')}
                  />
                </fieldset>
                <fieldset
                  disabled={
                    appliance !== undefined || !watch('hardwareManufacturerId')
                  }
                >
                  <FormModelSelect
                    {...register('hardwareModelId', {
                      required: true,
                    })}
                    manufacturer={watch('hardwareManufacturerId')}
                    type={watch('hardwareTypeId')}
                  />
                </fieldset>
                {watch('hardwareTypeId') ===
                  'aa6bfc6a-66a4-4113-af9a-1d0bdec5b8ae' && (
                  <HeatPumpSettings />
                )}
                {watch('deviceId') !== '0' && <ModbusSettings />}
                <div className="mt-4 sm:col-span-2 space-x-4">
                  <Button type="submit">
                    {appliance ? 'Update' : 'Create'}
                  </Button>
                  <Button variant="outlined" onClick={handleClose}>
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </Form>
        </>
      ) : (
        <Alert
          severity="error"
          title="Error getting device information"
          message="We were unable to retrieve device information, please try again"
        />
      )}
    </Modal>
  );
}
